/**
 * The visitor session, mirrored into `sessionStorage` on the widget origin.
 *
 * WHY THIS EXISTS AT ALL. `POST /v1/widget/session` is rate tier T1 — the
 * tightest in the product — and the contract says out loud that a bootstrap
 * "also costs a person row". The widget's iframe is destroyed and recreated on
 * EVERY navigation of the customer's site: browsing four product pages would
 * otherwise mint four tokens and four person rows for one human who never opened
 * the panel. Caching the session for the life of the tab turns that back into
 * one, and it is also what lets a visitor walk from a product page to the basket
 * without the pre-chat form asking their name again.
 *
 * WHY sessionStorage IS ACCEPTABLE HERE AND IS NOT IN THE AGENT CONSOLE.
 *
 *   1. DIFFERENT ORIGIN. This bundle runs on `widget.<domain>` inside a sandboxed
 *      iframe. Web storage is partitioned by origin, so the customer's page — and
 *      anything that has compromised it — cannot read this key. The console's
 *      token would sit on an origin that serves the whole agent application; this
 *      one sits on an origin that serves exactly one small app, ours.
 *
 *   2. DIFFERENT BLAST RADIUS. This token authorises one visitor to read their
 *      OWN conversation. Not a tenant's inbox, not another visitor's thread:
 *      `rlsr_conversation__app_runtime__visitor_scope` and
 *      `rlsr_message__app_runtime__visitor_scope` are RESTRICTIVE policies, so
 *      they combine with AND and can only ever narrow. The worst case for a
 *      stolen widget token is the transcript its owner already has open.
 *
 *   3. NO SAFER ALTERNATIVE EXISTS. The console can afford memory-only storage
 *      because it has an httpOnly refresh cookie to rebuild from. A widget has no
 *      login and — by the contract's own decision — no cookie, because a
 *      third-party cookie is removed by the browser or by an ad blocker. Memory
 *      only would mean the T1 bootstrap above on every page view. "Safer" that
 *      costs the visitor their conversation is not safer.
 *
 * `sessionStorage` and not `localStorage`: this dies with the tab. A website
 * visitor is frequently on a shared or public machine, and a support transcript
 * that outlives the browsing session on a library computer is a real disclosure.
 */
import type { WidgetAppearance, WidgetPrechatField } from './contract';

/**
 * How much of `expiresAt` we refuse to use.
 *
 * A token that expires in four seconds is not worth adopting: the request it
 * would authorise has not left the machine yet. Treating the last minute as
 * already gone turns a race into a bootstrap.
 */
const EXPIRY_MARGIN_MS = 60_000;

export interface CachedSession {
  readonly token: string;
  /** ISO-8601, straight from the session response. */
  readonly expiresAt: string;
  /**
   * The locale this session was bootstrapped for. Part of the record rather than
   * part of the key: a locale change must produce ONE session in the new
   * language, not a second session cached alongside the first.
   */
  readonly locale: string;
  readonly appearance: WidgetAppearance;
  readonly prechatFields: readonly WidgetPrechatField[];
  /**
   * The `sub` the session was minted for when it is a signed-in player's
   * (ADR-0249 §4), or null for an anonymous visitor. Read from the JWT WITHOUT
   * verifying it — the server did that — and used only to recognise "same
   * player again" on the next page view. Absent in a record written before this
   * field existed, which reads as anonymous.
   */
  readonly subject: string | null;
}

/** Namespaced by public key: two brands' widgets on one page must not share a session. */
function storageKey(publicKey: string): string {
  return `omni.widget.session.${publicKey}`;
}

function storage(): Storage | null {
  // Access itself THROWS in some privacy modes rather than returning null. A lost
  // cache costs one extra bootstrap; an exception costs the visitor the widget.
  try {
    return window.sessionStorage;
  } catch (unavailable) {
    if (unavailable instanceof Error) {
      return null;
    }
    throw unavailable;
  }
}

/**
 * Narrow what came out of storage without trusting it.
 *
 * The value was written by an earlier version of this app, in a tab that may
 * have been open across a deploy. Treating it as the current shape is how a
 * rename becomes a white panel; the cache is discarded instead, which costs one
 * bootstrap.
 */
function parseCached(raw: string): CachedSession | null {
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch (notJson) {
    if (notJson instanceof SyntaxError) {
      return null;
    }
    throw notJson;
  }

  if (typeof value !== 'object' || value === null) {
    return null;
  }
  // `Record<string, unknown>` and NOT `Partial<CachedSession>`. The cast used to
  // claim the parsed value already had the right field types, which made the
  // checks below look redundant to the compiler — `appearance === null` was
  // reported as having "no overlap" precisely because the cast had promised it
  // could not be null. It can: this string came out of sessionStorage, where any
  // script on this origin, or a previous version of this app, could have put
  // anything. Describing it as unknown is what makes the validation real instead
  // of decorative.
  const candidate = value as Record<string, unknown>;
  const { token, expiresAt, locale, appearance, prechatFields, subject } = candidate;

  if (
    typeof token !== 'string' ||
    typeof expiresAt !== 'string' ||
    typeof locale !== 'string' ||
    typeof appearance !== 'object' ||
    appearance === null ||
    // `Array.isArray` already excludes `undefined`; the extra test read as caution
    // and was noise.
    !Array.isArray(prechatFields) ||
    (subject !== undefined && subject !== null && typeof subject !== 'string')
  ) {
    return null;
  }

  // The field CONTENTS are not re-validated. They were validated by the server
  // that produced them and written by this app, and anyone who can edit them can
  // already edit the token sitting next to them — a deep check here would be
  // ceremony, not defence. What is checked is the SHAPE, because that is what a
  // deploy in a long-lived tab actually changes.
  // The two casts are that decision made explicit. The checks above establish
  // `appearance` is a non-null object and `prechatFields` is an array; asserting
  // their element types is the part deliberately not verified, and a cast on one
  // line says so more honestly than a `Partial<CachedSession>` cast further up
  // that would also have silently disabled the null check.
  return {
    token,
    expiresAt,
    locale,
    appearance: appearance as CachedSession['appearance'],
    prechatFields: prechatFields as CachedSession['prechatFields'],
    subject: typeof subject === 'string' ? subject : null,
  };
}

/** A cached session that is still worth using, or null. */
export function readCachedSession(publicKey: string, now: number): CachedSession | null {
  const store = storage();
  if (store === null) {
    return null;
  }

  const raw = store.getItem(storageKey(publicKey));
  if (raw === null) {
    return null;
  }

  const cached = parseCached(raw);
  if (cached === null) {
    return null;
  }

  const expiresAt = Date.parse(cached.expiresAt);
  if (Number.isNaN(expiresAt) || expiresAt - EXPIRY_MARGIN_MS <= now) {
    return null;
  }
  return cached;
}

export function writeCachedSession(publicKey: string, session: CachedSession): void {
  const store = storage();
  if (store === null) {
    return;
  }
  try {
    store.setItem(storageKey(publicKey), JSON.stringify(session));
  } catch (quotaOrPrivacy) {
    // Storage is full, or this browser counts `setItem` as tracking. Neither is
    // worth failing the widget over; the cost is one bootstrap per page view.
    if (!(quotaOrPrivacy instanceof Error)) {
      throw quotaOrPrivacy;
    }
  }
}

/** Called when the API rejects the token: a cached credential that 401s is worse than none. */
export function clearCachedSession(publicKey: string): void {
  const store = storage();
  if (store === null) {
    return;
  }
  store.removeItem(storageKey(publicKey));
}
